import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Facebook, Linkedin } from "lucide-react";
import { openCookiePreferences } from "./CookieConsent";
import { useLanguage } from "../i18n/LanguageContext";
import { useContent } from "../content/siteContent";

export default function Footer() {
  const { t } = useLanguage();
  const { content } = useContent();
  const f = t.footer;
  const year = new Date().getFullYear();
  const email = content.contactEmail;

  const cols = [
    {
      heading: f.product,
      links: [
        { label: f.links.platform, href: "#unified" },
        { label: f.links.how, href: "#journey" },
        { label: f.links.why, href: "#why-nepal" },
      ],
    },
    {
      heading: f.company,
      links: [
        { label: f.links.investors, href: "#investors" },
        { label: f.links.demo, href: "#demo" },
        { label: f.links.contact, href: `mailto:${email}` },
      ],
    },
  ];

  const socials = [
    content.facebookUrl && { icon: Facebook, href: content.facebookUrl, label: "Facebook" },
    content.linkedinUrl && { icon: Linkedin, href: content.linkedinUrl, label: "LinkedIn" },
  ].filter(Boolean);

  return (
    <footer data-testid="site-footer" className="text-white pt-16 pb-10" style={{ background: "#0A2038" }}>
      <div className="shell">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="col-span-2 lg:col-span-5">
            <div className="inline-flex items-baseline gap-2">
              <span className="font-display text-[20px] font-semibold tracking-tight text-white">
                Sajilo<span className="text-green">Health</span>
              </span>
              <span className="font-deva text-[12px] text-white/50">सजिलो ल्याब्स</span>
            </div>
            <p className="mt-5 text-[14px] text-white/55 max-w-sm leading-[1.65]">{f.blurb}</p>

            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-2.5">
                {socials.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      data-testid={`footer-${s.label.toLowerCase()}`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/12 bg-white/[0.04] text-white/70 hover:text-white hover:border-white/25 transition-colors"
                    >
                      <Icon size={15} strokeWidth={1.9} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {cols.map((col) => (
            <div key={col.heading} className="lg:col-span-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{col.heading}</div>
              <ul className="mt-5 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-[13.5px] text-white/65 hover:text-white transition-colors">{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{f.reach}</div>
            <div className="mt-5 space-y-3">
              <a href={`mailto:${email}`} data-testid="footer-mailto" className="inline-flex items-center gap-2.5 text-[13.5px] text-white/65 hover:text-white transition-colors">
                <Mail size={15} strokeWidth={1.8} className="text-green" />
                {email}
              </a>
              <div className="flex items-center gap-2.5 text-[13.5px] text-white/65">
                <MapPin size={15} strokeWidth={1.8} className="text-green" />
                Pokhara, Nepal
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[12px] text-white/45">
            © {year} Sajilo Labs Pvt. Ltd. {f.rights}
            <span className="mx-2 text-white/20">—</span>
            <span className="font-mono tracking-wide">{f.registered}</span>
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-link-privacy" className="text-[12px] font-medium text-white/50 hover:text-white transition-colors">{f.privacy}</Link>
            <Link to="/terms" data-testid="footer-link-terms" className="text-[12px] font-medium text-white/50 hover:text-white transition-colors">{f.terms}</Link>
            <button type="button" onClick={openCookiePreferences} data-testid="footer-link-cookies" className="text-[12px] font-medium text-white/50 hover:text-white transition-colors">{f.cookies}</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
